import React, { useState, useEffect, useCallback } from 'react';
import { verifyMyPassword } from './lib/verifyPassword';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import {
  BookOpen, Search, UserPlus, UserMinus, X, CheckCircle, Clock,
  ChevronDown, RefreshCw, Users, Mail, Calendar, Shield, Trash2, Lock, Plus, Layers
} from 'lucide-react';
import { friendlyError, notifyError, readJson } from './lib/errors';
import { confirmDialog } from './components/ConfirmDialog';
import Loader from './components/Loader';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function api(path, opts = {}) {
  const token = localStorage.getItem('desky_token');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json', ...opts.headers };
  return fetch(`${API_URL}${path}`, { ...opts, headers }).then(readJson);
}

/* ══════════════════════════════════════════════════════════════
   ClassSubjectsTab — Inside class detail view
   Shows subjects for a class with assign teacher + substitute
   ══════════════════════════════════════════════════════════════ */
export function ClassSubjectsTab({ classId, accent = '#34A853' }) {
  const [subjectRows, setSubjectRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAssign, setShowAssign] = useState(null);
  const [showSubstitute, setShowSubstitute] = useState(null);
  const [showUnassignSub, setShowUnassignSub] = useState(null);
  const [showPicker, setShowPicker] = useState(false);
  const [pickerQuery, setPickerQuery] = useState('');
  const [pickerResults, setPickerResults] = useState([]);
  const [linking, setLinking] = useState(false);
  const A = accent;

  const load = useCallback(async () => {
    try {
      const data = await api(`/api/classes/${classId}/subjects`);
      setSubjectRows(data || []);
    } catch {}
    setLoading(false);
  }, [classId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!showPicker) return;
    const t = setTimeout(async () => {
      try { setPickerResults(await api(`/api/subjects?q=${encodeURIComponent(pickerQuery)}`) || []); } catch {}
    }, 250);
    return () => clearTimeout(t);
  }, [showPicker, pickerQuery]);

  const handleLink = async (subjectId) => {
    setLinking(true);
    try {
      await api(`/api/subjects/${subjectId}/links`, { method: 'POST', body: JSON.stringify({ class_id: classId }) });
      setShowPicker(false); setPickerQuery(''); load();
    } catch (e) { notifyError(e, "Couldn't add the subject to this class."); }
    setLinking(false);
  };

  if (loading) return <Loader />;

  return (
    <div className="space-y-4" data-testid="class-subjects-tab">
      {/* Select Subject */}
      <div className="flex items-center gap-2">
        {showPicker ? (
          <div className="flex items-center gap-2 flex-1">
            <div className="relative flex-1">
              <Input
                value={pickerQuery}
                onChange={e => setPickerQuery(e.target.value)}
                placeholder="Search subjects…"
                className="h-9 flex-1 rounded-lg text-sm"
                autoFocus
                autoComplete="off"
                data-testid="subject-picker-search"
              />
              {pickerResults.length > 0 && (
                <div className="absolute left-0 right-0 top-full mt-1 bg-white rounded-lg border border-gray-200 shadow-lg z-10 max-h-[180px] overflow-y-auto" data-testid="subject-picker-results">
                  {pickerResults.map(s => (
                    <button
                      key={s.id}
                      type="button"
                      disabled={linking}
                      onClick={() => handleLink(s.id)}
                      className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      data-testid={`subject-picker-option-${s.id}`}
                    >
                      {s.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button onClick={() => { setShowPicker(false); setPickerQuery(''); }} className="text-xs text-gray-500 px-2">Cancel</button>
          </div>
        ) : (
          <Button onClick={() => setShowPicker(true)} variant="outline" size="sm" className="h-8 px-3 rounded-lg text-xs border-dashed border-gray-300 text-gray-500 hover:text-gray-700 hover:border-gray-400" data-testid="select-subject-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Select Subject
          </Button>
        )}
      </div>

      {subjectRows.length === 0 ? (
        <div className="bg-white rounded-xl border-2 border-dashed border-gray-200 p-10 text-center">
          <BookOpen className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-sm font-bold text-gray-500">No subjects in this class</p>
          <p className="text-xs text-gray-400 mt-1">Select a subject and assign a teacher</p>
        </div>
      ) : (
        <div className="space-y-3">
          {subjectRows.map(s => {
            const hasTeacher = !!s.teacher_id;
            const activeSubs = (s.active_substitutions || []).filter(x => x.status === 'active');
            const endedSubs = (s.active_substitutions || []).filter(x => x.status !== 'active');
            return (
              <div key={s.id} className="bg-white rounded-xl border border-gray-200 p-4 space-y-3" data-testid={`class-subject-${s.id}`}>
                {/* Subject header + teacher */}
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${A}14` }}>
                    <BookOpen className="w-4 h-4" style={{ color: A }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-gray-900">{s.name}</p>
                    {hasTeacher ? (
                      <p className="text-[11px] text-gray-500 flex items-center gap-1">
                        <Users className="w-3 h-3" /> {s.teacher_name} <span className="text-gray-300">&middot;</span> {s.teacher_email}
                      </p>
                    ) : (
                      <p className="text-[11px] text-amber-500 font-medium">No teacher assigned</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Button onClick={() => setShowAssign({ subjectId: s.subject_id, linkId: s.id })} size="sm" className="h-7 px-2.5 rounded-lg text-white text-[11px] active:scale-95" style={{ background: A }} data-testid={`assign-teacher-${s.id}`}>
                      <UserPlus className="w-3 h-3 mr-1" /> {hasTeacher ? 'Reassign' : 'Assign'}
                    </Button>
                    <Button onClick={() => setShowSubstitute({ subjectId: s.subject_id, linkId: s.id })} variant="outline" size="sm" className="h-7 px-2.5 rounded-lg text-[11px] border-blue-200 text-blue-600 hover:bg-blue-50" data-testid={`assign-sub-${s.id}`}>
                      <Shield className="w-3 h-3 mr-1" /> Substitute
                    </Button>
                  </div>
                </div>

                {/* Active Substitutions */}
                {activeSubs.length > 0 && (
                  <div className="pl-12 space-y-2">
                    {activeSubs.map(sub => (
                      <div key={sub.id} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-50 border border-blue-100" data-testid={`sub-${sub.id}`}>
                        <Shield className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-[11px] font-semibold text-gray-900 flex items-center gap-1.5 flex-wrap">
                            {sub.substitute_teacher_name} <span className="text-blue-500 font-normal">&middot; Substitute</span>
                            {sub.accepted ? (
                              <span className="text-[9px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded-full" data-testid={`sub-accepted-${sub.id}`}>Accepted</span>
                            ) : (
                              <span className="text-[9px] font-bold uppercase tracking-wider text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded-full" data-testid={`sub-pending-${sub.id}`}>Awaiting acceptance</span>
                            )}
                            {sub.surrogate_session_active && (
                              <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full" data-testid={`sub-live-${sub.id}`}>
                                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" /> Acting now
                              </span>
                            )}
                          </p>
                          <p className="text-[10px] text-gray-500 flex items-center gap-1"><Calendar className="w-3 h-3" /> {sub.start_date} &mdash; {sub.end_date} {sub.reason && `&middot; ${sub.reason}`}</p>
                        </div>
                        <span className="text-[9px] text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded">{sub.lectures_posted || 0} lectures</span>
                        <button onClick={() => setShowUnassignSub(sub)} className="p-1 rounded hover:bg-red-50 text-gray-300 hover:text-red-500" data-testid={`unassign-sub-${sub.id}`} title="Unassign substitute">
                          <UserMinus className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Ended / Unassigned Substitutions */}
                {endedSubs.length > 0 && (
                  <div className="pl-12 space-y-1.5">
                    <p className="text-[9px] font-semibold text-gray-400 uppercase tracking-wider">Past substitutions</p>
                    {endedSubs.map(sub => (
                      <div key={sub.id} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 border border-gray-100 opacity-60" data-testid={`ended-sub-${sub.id}`}>
                        <Shield className="w-3 h-3 text-gray-400 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-[10px] text-gray-600">{sub.substitute_teacher_name} &middot; {sub.start_date} &mdash; {sub.end_date}</p>
                        </div>
                        <span className="text-[9px] text-gray-400 px-1.5 py-0.5 rounded bg-gray-100">{sub.status}</span>
                        <button onClick={async () => {
                          if (!(await confirmDialog({ title: 'Delete substitution record', message: "Delete this substitution record? This can't be undone.", danger: true, confirmLabel: 'Delete' }))) return;
                          await api(`/api/substitutions/${sub.id}`, { method: 'DELETE' });
                          load();
                        }} className="p-1 rounded hover:bg-red-50 text-gray-300 hover:text-red-500" data-testid={`delete-sub-${sub.id}`} title="Delete record">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Modals */}
      {showAssign && <TeacherAssignModal subjectId={showAssign.subjectId} linkId={showAssign.linkId} accent={A} onClose={() => setShowAssign(null)} onAssigned={() => { setShowAssign(null); load(); }} />}
      {showSubstitute && <SubstituteAssignModal subjectId={showSubstitute.subjectId} linkId={showSubstitute.linkId} accent={A} onClose={() => setShowSubstitute(null)} onAssigned={() => { setShowSubstitute(null); load(); }} />}
      {showUnassignSub && <UnassignSubstituteModal sub={showUnassignSub} accent={A} onClose={() => setShowUnassignSub(null)} onUnassigned={() => { setShowUnassignSub(null); load(); }} />}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   SubjectManagement — Top-level for Institution Dashboard  
   Batch -> Class -> Subjects (same compact style as Dept Dashboard)
   ══════════════════════════════════════════════════════════════ */
export function SubjectManagement({ accent = '#34A853' }) {
  const [batches, setBatches] = useState([]);
  const [classes, setClasses] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [selectedClass, setSelectedClass] = useState(null);
  const [loading, setLoading] = useState(true);
  const [batchSearch, setBatchSearch] = useState('');
  const [showBatchForm, setShowBatchForm] = useState(false);
  const [batchName, setBatchName] = useState('');
  const [showClassForm, setShowClassForm] = useState(false);
  const [classForm, setClassForm] = useState({ name: '', code: '', capacity: 30 });
  const A = accent;

  const load = useCallback(async () => {
    try {
      const [b, c] = await Promise.all([api('/api/batches'), api('/api/classes')]);
      setBatches(b || []);
      setClasses(c || []);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveBatch = async () => {
    if (!batchName.trim()) return;
    await api('/api/batches', { method: 'POST', body: JSON.stringify({ name: batchName.trim() }) });
    setBatchName(''); setShowBatchForm(false); load();
  };

  const deleteBatch = async (id) => {
    if (!(await confirmDialog({ title: 'Delete batch', message: 'Delete this batch?', danger: true, confirmLabel: 'Delete' }))) return;
    await api(`/api/batches/${id}`, { method: 'DELETE' });
    load();
  };

  const saveClass = async () => {
    if (!classForm.name.trim()) return;
    await api('/api/classes', { method: 'POST', body: JSON.stringify({ ...classForm, batch_id: selectedBatch?.id || '' }) });
    setClassForm({ name: '', code: '', capacity: 30 }); setShowClassForm(false); load();
  };

  if (loading) return <Loader />;

  // Level 3: Inside a class — show subjects
  if (selectedClass) {
    return (
      <div data-testid="subject-management">
        <button onClick={() => setSelectedClass(null)} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4"><X className="w-3.5 h-3.5" /> Back to {selectedBatch?.name || 'classes'}</button>
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${A}14` }}><BookOpen className="w-4 h-4" style={{ color: A }} /></div>
          <div><p className="text-sm font-bold text-gray-900">{selectedClass.name}</p><p className="text-[10px] text-gray-400 font-mono">{selectedClass.class_code || selectedClass.code}</p></div>
        </div>
        <ClassSubjectsTab classId={selectedClass.id} accent={A} />
      </div>
    );
  }

  // Level 2: Inside a batch — show classes
  if (selectedBatch) {
    const bc = classes.filter(c => c.batch_id === selectedBatch.id);
    return (
      <div data-testid="subject-management">
        <div className="flex items-center gap-3 mb-5">
          <button onClick={() => setSelectedBatch(null)} className="p-2 rounded-lg text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-all"><X className="w-4 h-4" /></button>
          <div className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold text-white" style={{ background: A }}>{selectedBatch.name?.[0] || 'B'}</div>
          <div><h3 className="text-base font-bold text-gray-900">{selectedBatch.name}</h3><p className="text-[11px] text-gray-400">{bc.length} classes</p></div>
          <div className="ml-auto">
            <Button onClick={() => setShowClassForm(true)} className="h-9 px-4 rounded-lg text-white text-[13px] font-semibold" style={{ background: A }}><Plus className="w-3.5 h-3.5 mr-1.5" /> New Class</Button>
          </div>
        </div>
        {showClassForm && (
          <div className="flex items-center gap-3 p-3 bg-white rounded-xl border-2 border-dashed mb-4" style={{ borderColor: `${A}40` }}>
            <Input value={classForm.name} onChange={e => setClassForm(p => ({ ...p, name: e.target.value }))} placeholder="Class name" className="h-9 flex-1 rounded-lg text-sm" autoFocus onKeyDown={e => e.key === 'Enter' && saveClass()} />
            <Input value={classForm.code} onChange={e => setClassForm(p => ({ ...p, code: e.target.value }))} placeholder="Code" className="h-9 w-28 rounded-lg text-sm font-mono" />
            <Button onClick={saveClass} disabled={!classForm.name.trim()} className="h-9 px-4 rounded-lg text-white text-xs" style={{ background: A }}>Create</Button>
            <button onClick={() => setShowClassForm(false)} className="text-xs text-gray-400 px-2">Cancel</button>
          </div>
        )}
        {bc.length === 0 ? (
          <div className="text-center py-14"><BookOpen className="w-10 h-10 mx-auto mb-3" style={{ color: `${A}30` }} /><p className="text-sm text-gray-500">No classes in this batch</p></div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            {bc.map(c => (
              <div key={c.id} onClick={() => setSelectedClass(c)} className="bg-white rounded-xl border border-gray-200 p-4 cursor-pointer group hover:shadow-md hover:-translate-y-[1px] transition-all relative" data-testid={`class-pick-${c.id}`}>
                <div className="absolute top-0 left-0 w-full h-1 rounded-t-xl" style={{ background: A }} />
                <div className="w-8 h-8 rounded-lg flex items-center justify-center mb-2.5" style={{ background: `${A}12` }}><BookOpen className="w-4 h-4" style={{ color: A }} /></div>
                <h4 className="text-[13px] font-bold text-gray-900 truncate">{c.name}</h4>
                <p className="text-[10px] text-gray-400 font-mono mt-0.5">{c.class_code || c.code}</p>
                <span className="text-[10px] font-semibold mt-1.5 block" style={{ color: A }}>{c.student_count || 0} students</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Level 1: Batch grid
  const filtered = batches.filter(b => !batchSearch || b.name?.toLowerCase().includes(batchSearch.toLowerCase()));
  const unassigned = classes.filter(c => !c.batch_id || !batches.find(b => b.id === c.batch_id));

  return (
    <div data-testid="subject-management">
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-xs">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input value={batchSearch} onChange={e => setBatchSearch(e.target.value)} placeholder="Search batches..." className="h-9 pl-9 rounded-lg text-sm" data-testid="batch-search" />
        </div>
        <span className="text-[11px] text-gray-400">{batches.length} batch{batches.length !== 1 ? 'es' : ''}</span>
        <div className="ml-auto">
          <Button onClick={() => setShowBatchForm(true)} className="h-9 px-4 rounded-lg text-white text-[13px] font-semibold" style={{ background: A }} data-testid="create-batch-btn"><Plus className="w-3.5 h-3.5 mr-1.5" /> New Batch</Button>
        </div>
      </div>

      {showBatchForm && (
        <div className="flex items-center gap-3 p-3 bg-white rounded-xl border-2 border-dashed mb-4" style={{ borderColor: `${A}40` }} data-testid="batch-form">
          <Input value={batchName} onChange={e => setBatchName(e.target.value)} placeholder="Batch name, e.g. Grade 10" className="h-9 flex-1 rounded-lg text-sm" autoFocus data-testid="batch-name-input" onKeyDown={e => e.key === 'Enter' && saveBatch()} />
          <Button onClick={saveBatch} disabled={!batchName.trim()} className="h-9 px-4 rounded-lg text-white text-xs" style={{ background: A }} data-testid="save-batch-btn">Create</Button>
          <button onClick={() => setShowBatchForm(false)} className="text-xs text-gray-400 px-2">Cancel</button>
        </div>
      )}

      {filtered.length === 0 && unassigned.length === 0 ? (
        <div className="text-center py-14"><Layers className="w-10 h-10 mx-auto mb-3" style={{ color: `${A}30` }} /><p className="text-sm text-gray-500">{batches.length === 0 ? 'No batches yet' : 'No results'}</p></div>
      ) : (
        <div className="space-y-5">
          {filtered.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
              {filtered.map(batch => {
                const bc = classes.filter(c => c.batch_id === batch.id);
                return (
                  <div key={batch.id} onClick={() => setSelectedBatch(batch)} className="bg-white rounded-xl border border-gray-200 p-4 cursor-pointer group hover:shadow-md hover:-translate-y-[1px] transition-all relative" data-testid={`batch-card-${batch.id}`}>
                    <div className="absolute top-0 left-0 w-full h-1 rounded-t-xl" style={{ background: A }} />
                    <button onClick={(e) => { e.stopPropagation(); deleteBatch(batch.id); }} className="absolute top-2 right-2 p-1 rounded-lg opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-500 hover:bg-red-50 transition-all"><Trash2 className="w-3 h-3" /></button>
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold text-white mb-2.5" style={{ background: A }}>{batch.name?.[0] || 'B'}</div>
                    <h4 className="text-[13px] font-bold text-gray-900 truncate leading-tight">{batch.name}</h4>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-[10px] font-semibold" style={{ color: A }}>{bc.length} class{bc.length !== 1 ? 'es' : ''}</span>
                      <span className="text-[10px] text-gray-400">{bc.reduce((a, c) => a + (c.student_count || 0), 0)} students</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {unassigned.length > 0 && (
            <div>
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Unassigned Classes</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                {unassigned.map(c => (
                  <div key={c.id} onClick={() => setSelectedClass(c)} className="bg-white rounded-xl border border-gray-200 p-4 cursor-pointer group hover:shadow-md hover:-translate-y-[1px] transition-all relative" data-testid={`class-pick-${c.id}`}>
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center mb-2.5" style={{ background: `${A}12` }}><BookOpen className="w-4 h-4" style={{ color: A }} /></div>
                    <h4 className="text-[13px] font-bold text-gray-900 truncate">{c.name}</h4>
                    <p className="text-[10px] text-gray-400 font-mono mt-0.5">{c.class_code || c.code}</p>
                    <span className="text-[10px] font-semibold mt-1.5 block" style={{ color: A }}>{c.student_count || 0} students</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── Teacher Assign Modal (with 2FA confirmation) ─── */
function TeacherAssignModal({ subjectId, linkId, accent, onClose, onAssigned }) {
  const [query, setQuery] = useState('');
  const [teachers, setTeachers] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const t = setTimeout(async () => {
      setSearching(true);
      try { setTeachers(await api(`/api/teachers/search?q=${encodeURIComponent(query)}`) || []); } catch {}
      setSearching(false);
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  const handleConfirmAssign = async () => {
    if (!selected || !confirmPassword) return;
    setAssigning(true); setError('');
    try {
      const authRes = await verifyMyPassword(confirmPassword);
      if (!authRes.ok) { setError(friendlyError(await authRes.json().catch(() => ({})), "Couldn't check your password.")); setAssigning(false); return; }
      await api(`/api/subjects/${subjectId}/links/${linkId}/assign-teacher`, { method: 'POST', body: JSON.stringify({ teacher_id: selected.id }) });
      onAssigned();
    } catch (e) { setError(friendlyError(e, 'Assignment failed.')); }
    setAssigning(false);
  };

  return (
    <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-center justify-center" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 w-[480px] shadow-2xl max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()} data-testid="assign-teacher-modal">
        {!selected ? (
          <>
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Assign Teacher</h3>
            <p className="text-sm text-gray-500 mb-4">Search by name, email, or ID</p>
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search teachers..." className="h-10 pl-10 rounded-xl" autoFocus data-testid="teacher-search-input" />
            </div>
            <div className="flex-1 overflow-y-auto space-y-1 min-h-[150px] max-h-[300px]">
              {searching ? <Loader />
              : teachers.map(t => (
                <button key={t.id} onClick={() => setSelected(t)} className="w-full flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-gray-50 text-left" data-testid={`teacher-option-${t.id}`}>
                  <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold text-white flex-shrink-0" style={{ background: accent }}>{(t.full_name || '?')[0]}</div>
                  <div className="flex-1"><p className="text-sm font-semibold text-gray-900">{t.full_name}</p><p className="text-[11px] text-gray-500">{t.email} &middot; {t.id}</p></div>
                  <ChevronDown className="w-4 h-4 text-gray-300 -rotate-90" />
                </button>
              ))}
            </div>
          </>
        ) : (
          <>
            <h3 className="text-lg font-semibold text-gray-900 mb-1 flex items-center gap-2"><Lock className="w-5 h-5" style={{ color: accent }} /> Confirm Assignment</h3>
            <p className="text-sm text-gray-500 mb-4">Enter your password to confirm this assignment</p>
            <div className="bg-gray-50 rounded-xl p-3 mb-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold text-white" style={{ background: accent }}>{(selected.full_name || '?')[0]}</div>
              <div><p className="text-sm font-bold text-gray-900">{selected.full_name}</p><p className="text-[11px] text-gray-500">{selected.email}</p></div>
              <button onClick={() => { setSelected(null); setConfirmPassword(''); setError(''); }} className="ml-auto p-1 rounded hover:bg-gray-200"><X className="w-4 h-4 text-gray-400" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider block mb-1">Your Password (2FA)</label>
                <Input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Enter your password to confirm" className="h-10 rounded-xl" data-testid="confirm-password" onKeyDown={e => e.key === 'Enter' && handleConfirmAssign()} />
              </div>
              {error && <p className="text-xs text-red-500">{error}</p>}
              <Button onClick={handleConfirmAssign} disabled={assigning || !confirmPassword} className="w-full h-10 rounded-xl text-white text-sm font-medium active:scale-95 disabled:opacity-50" style={{ background: accent }} data-testid="confirm-assign-btn">
                {assigning ? <><RefreshCw className="w-4 h-4 animate-spin mr-2" /> Verifying...</> : <><Lock className="w-4 h-4 mr-2" /> Confirm & Assign</>}
              </Button>
            </div>
          </>
        )}
        <div className="flex justify-end mt-4 pt-3 border-t border-gray-100">
          <button onClick={onClose} className="text-sm text-gray-500 px-4 py-2 rounded-xl hover:bg-gray-50">Cancel</button>
        </div>
      </div>
    </div>
  );
}

/* ─── Substitute Assign Modal (with 2FA + date range + calendar) ─── */
function SubstituteAssignModal({ subjectId, linkId, accent, onClose, onAssigned }) {
  const [query, setQuery] = useState('');
  const [teachers, setTeachers] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState(1); // 1=search, 2=details, 3=2FA

  useEffect(() => {
    const t = setTimeout(async () => {
      setSearching(true);
      try { setTeachers(await api(`/api/teachers/search?q=${encodeURIComponent(query)}`) || []); } catch {}
      setSearching(false);
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  const handleConfirm = async () => {
    if (!selected || !startDate || !endDate || !confirmPassword) return;
    setAssigning(true); setError('');
    try {
      const authRes = await verifyMyPassword(confirmPassword);
      if (!authRes.ok) { setError(friendlyError(await authRes.json().catch(() => ({})), "Couldn't check your password.")); setAssigning(false); return; }
      await api(`/api/subjects/${subjectId}/links/${linkId}/assign-substitute`, {
        method: 'POST',
        body: JSON.stringify({ substitute_teacher_id: selected.id, start_date: startDate, end_date: endDate, reason, password: confirmPassword }),
      });
      onAssigned();
    } catch (e) { setError(friendlyError(e, 'Assignment failed.')); }
    setAssigning(false);
  };

  return (
    <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-center justify-center" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 w-[500px] shadow-2xl max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()} data-testid="assign-substitute-modal">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center"><Shield className="w-5 h-5 text-blue-600" /></div>
          <div className="flex-1">
            <h3 className="text-base font-bold text-gray-900">Assign Substitute Teacher</h3>
            <p className="text-[11px] text-gray-500">Step {step} of 3 &middot; {step === 1 ? 'Select Teacher' : step === 2 ? 'Set Period' : 'Confirm (2FA)'}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100"><X className="w-4 h-4 text-gray-400" /></button>
        </div>

        {/* Progress Bar */}
        <div className="flex gap-1 mb-5">
          {[1,2,3].map(s => (
            <div key={s} className="h-1 flex-1 rounded-full transition-all" style={{ background: s <= step ? '#3B82F6' : '#E5E7EB' }} />
          ))}
        </div>

        {/* Step 1: Search Teacher */}
        {step === 1 && (
          <>
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search teacher by name or email..." className="h-10 pl-10 rounded-xl" autoFocus data-testid="sub-search-input" />
            </div>
            <div className="flex-1 overflow-y-auto space-y-1 min-h-[150px] max-h-[280px]">
              {searching ? <Loader />
              : teachers.map(t => (
                <button key={t.id} onClick={() => { setSelected(t); setStep(2); }} className="w-full flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-blue-50 text-left transition-colors" data-testid={`sub-teacher-${t.id}`}>
                  <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center text-sm font-bold text-blue-700">{(t.full_name || '?')[0]}</div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-gray-900">{t.full_name}</p>
                    <p className="text-[11px] text-gray-500">{t.email} &middot; <span className="font-mono text-gray-400">{t.id}</span></p>
                  </div>
                  <ChevronDown className="w-4 h-4 text-gray-300 -rotate-90" />
                </button>
              ))}
            </div>
          </>
        )}

        {/* Step 2: Set dates & reason */}
        {step === 2 && selected && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-3 rounded-xl bg-blue-50 border border-blue-100">
              <div className="w-10 h-10 rounded-full bg-blue-200 flex items-center justify-center text-sm font-bold text-blue-800">{(selected.full_name || '?')[0]}</div>
              <div className="flex-1"><p className="text-sm font-bold text-gray-900">{selected.full_name}</p><p className="text-[11px] text-gray-500">{selected.email}</p></div>
              <button onClick={() => { setSelected(null); setStep(1); }} className="p-1 rounded-lg hover:bg-blue-100 text-blue-400"><X className="w-4 h-4" /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider block mb-1">Start Date *</label>
                <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="h-10 rounded-xl text-sm" data-testid="sub-start-date" />
              </div>
              <div>
                <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider block mb-1">End Date *</label>
                <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="h-10 rounded-xl text-sm" data-testid="sub-end-date" />
              </div>
            </div>
            <div>
              <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider block mb-1">Reason (optional)</label>
              <Input value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g., Teacher on leave, medical absence" className="h-10 rounded-xl text-sm" data-testid="sub-reason" />
            </div>
            <Button onClick={() => setStep(3)} disabled={!startDate || !endDate} className="w-full h-10 rounded-xl text-white text-sm font-medium active:scale-95 disabled:opacity-50 bg-blue-600 hover:bg-blue-700" data-testid="sub-next-step">
              Continue to Verification
            </Button>
          </div>
        )}

        {/* Step 3: 2FA Confirmation */}
        {step === 3 && selected && (
          <div className="space-y-4">
            <div className="bg-blue-50/50 rounded-xl p-4 border border-blue-100">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-full bg-blue-200 flex items-center justify-center text-sm font-bold text-blue-800">{(selected.full_name || '?')[0]}</div>
                <div className="flex-1"><p className="text-sm font-bold text-gray-900">{selected.full_name}</p><p className="text-[10px] text-gray-500">{selected.email}</p></div>
              </div>
              <div className="flex gap-4 text-[11px] text-gray-600">
                <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {startDate} &mdash; {endDate}</span>
                {reason && <span className="text-gray-400">{reason}</span>}
              </div>
            </div>
            <div>
              <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider block mb-1 flex items-center gap-1"><Lock className="w-3 h-3" /> Your Password (2FA Verification)</label>
              <Input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Enter your password to confirm" className="h-10 rounded-xl" data-testid="sub-confirm-password" onKeyDown={e => e.key === 'Enter' && handleConfirm()} autoFocus />
            </div>
            {error && <p className="text-xs text-red-500">{error}</p>}
            <div className="flex gap-2">
              <button onClick={() => setStep(2)} className="h-10 px-4 rounded-xl text-sm text-gray-500 hover:bg-gray-50 border border-gray-200">Back</button>
              <Button onClick={handleConfirm} disabled={assigning || !confirmPassword} className="flex-1 h-10 rounded-xl text-white text-sm font-medium active:scale-95 disabled:opacity-50 bg-blue-600 hover:bg-blue-700" data-testid="confirm-sub-btn">
                {assigning ? <><RefreshCw className="w-4 h-4 animate-spin mr-2" /> Verifying...</> : <><Lock className="w-4 h-4 mr-2" /> Confirm & Assign</>}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── Unassign Substitute Modal (2FA) ─── */
function UnassignSubstituteModal({ sub, accent, onClose, onUnassigned }) {
  const [password, setPassword] = useState('');
  const [unassigning, setUnassigning] = useState(false);
  const [error, setError] = useState('');

  const handleConfirm = async () => {
    if (!password) return;
    setUnassigning(true); setError('');
    try {
      const res = await fetch(`${API_URL}/api/substitutions/${sub.id}/unassign`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('desky_token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(friendlyError(data, 'Verification failed'));
        setUnassigning(false);
        return;
      }
      onUnassigned();
    } catch { setError('Failed to unassign'); }
    setUnassigning(false);
  };

  return (
    <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-center justify-center" onClick={onClose}>
      <div className="bg-white rounded-2xl w-[400px] shadow-2xl border border-gray-100 p-5" onClick={e => e.stopPropagation()} data-testid="unassign-sub-modal">
        <div className="flex items-center gap-2.5 mb-4">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-red-50"><Shield className="w-4 h-4 text-red-500" /></div>
          <div>
            <h3 className="text-sm font-bold text-gray-900">Unassign Substitute</h3>
            <p className="text-[10px] text-gray-500">Remove {sub.substitute_teacher_name} from substitution</p>
          </div>
        </div>
        <div className="bg-blue-50/50 rounded-xl p-3 mb-4 border border-blue-100">
          <p className="text-[11px] font-semibold text-gray-900">{sub.substitute_teacher_name}</p>
          <p className="text-[10px] text-gray-500">{sub.subject_name} &middot; {sub.start_date} &mdash; {sub.end_date}</p>
        </div>
        <div className="mb-4">
          <label className="block text-[9px] font-semibold text-gray-400 uppercase tracking-wider mb-1 flex items-center gap-1"><Lock className="w-3 h-3" /> Your Password (2FA)</label>
          <Input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Enter password to confirm" className="h-9 rounded-lg text-sm" autoFocus data-testid="unassign-sub-pwd" onKeyDown={e => e.key === 'Enter' && handleConfirm()} />
        </div>
        {error && <p className="text-xs text-red-500 mb-2">{error}</p>}
        <div className="flex gap-2">
          <Button onClick={handleConfirm} disabled={unassigning || !password} className="flex-1 h-9 rounded-lg text-white text-xs font-semibold bg-red-500 hover:bg-red-600 disabled:opacity-50" data-testid="confirm-unassign-sub">
            {unassigning ? 'Removing...' : 'Confirm & Unassign'}
          </Button>
          <button onClick={onClose} className="text-xs text-gray-500 px-3">Cancel</button>
        </div>
      </div>
    </div>
  );
}

export default SubjectManagement;
